import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MonitorSmartphone, Droplets, Scale, CreditCard } from "lucide-react";
import { SectionReveal, ChapterHeading } from "./Shared";
import { useLang } from "../../i18n";

const PINS = [
  { id: "screen", icon: MonitorSmartphone, x: 46, y: 26 },
  { id: "payment", icon: CreditCard, x: 64, y: 33 },
  { id: "dosing", icon: Scale, x: 46, y: 61 },
  { id: "clean", icon: Droplets, x: 46, y: 86 },
];

export default function Hardware() {
  const { t } = useLang();
  const HOTSPOTS = PINS.map((p) => ({ ...p, ...t.hardware.hotspots[p.id] }));
  const SPECS = t.hardware.specs;
  const [active, setActive] = useState(HOTSPOTS[0]);

  const COMPACT_PINS = [
    { id: "screen", icon: MonitorSmartphone, x: 47, y: 37 },
    { id: "payment", icon: CreditCard, x: 73, y: 61 },
    { id: "dosing", icon: Scale, x: 45, y: 67 },
    { id: "clean", icon: Droplets, x: 46, y: 88 },
  ];
  const compact = t.hardware.compact;
  const COMPACT_SPOTS = COMPACT_PINS.map((p) => ({ ...p, ...compact.hotspots[p.id] }));
  const [activeCId, setActiveCId] = useState("screen");
  const activeC = COMPACT_SPOTS.find((s) => s.id === activeCId);

  return (
    <section id="donanim" className="mx-auto max-w-7xl px-5 py-28 sm:px-8" data-testid="hardware-section">
      <ChapterHeading
        index="03"
        label={t.hardware.label}
        title={t.hardware.title}
        sub={t.hardware.sub}
      />

      <div className="mt-16 grid items-start gap-10 lg:grid-cols-[1.05fr_1fr]">
        <SectionReveal>
          <div className="relative overflow-hidden rounded-[2rem] border border-[#9B7AE8]/20 bg-white shadow-[0_30px_90px_rgba(155,122,232,0.18)]" data-testid="hardware-machine-frame">
            <img src="/assets/machine.jpg" alt={t.hardware.alt} className="block w-full" />
            <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_55%,rgba(248,249,250,0.45)_100%)]" />
            {HOTSPOTS.map((h) => (
              <button
                key={h.id}
                data-testid={`hotspot-pin-${h.id}`}
                onClick={() => setActive(h)}
                className="group absolute -translate-x-1/2 -translate-y-1/2"
                style={{ left: `${h.x}%`, top: `${h.y}%` }}
                aria-label={h.title}
              >
                <span className={`animate-pin absolute inset-0 rounded-full ${active.id === h.id ? "bg-[#C9B0FF]/60" : "bg-[#9B7AE8]/35"}`} />
                <span
                  className={`relative flex h-9 w-9 items-center justify-center rounded-full border backdrop-blur-md transition-all duration-300 ${
                    active.id === h.id
                      ? "border-[#9B7AE8] bg-[#9B7AE8] text-white shadow-[0_8px_24px_rgba(155,122,232,0.55)]"
                      : "border-[#9B7AE8]/50 bg-white/85 text-[#9B7AE8] shadow-[0_6px_18px_rgba(155,122,232,0.25)] group-hover:bg-[#C9B0FF]/40"
                  }`}
                >
                  <h.icon size={15} strokeWidth={2} />
                </span>
              </button>
            ))}
          </div>
        </SectionReveal>

        <div className="space-y-6">
          <SectionReveal delay={0.1}>
            <AnimatePresence mode="wait">
              <motion.div
                key={active.id}
                data-testid="hotspot-detail-card"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -16 }}
                transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                className="rounded-2xl border border-[#C9B0FF] bg-white/85 p-7 shadow-[0_18px_50px_rgba(155,122,232,0.16)] backdrop-blur-sm"
              >
                <div className="flex items-center gap-3">
                  <active.icon size={22} className="text-[#9B7AE8]" strokeWidth={1.75} />
                  <h3 className="font-display text-xl font-bold text-[#221A3D]">{active.title}</h3>
                </div>
                <p className="mt-3 text-sm leading-relaxed text-[#5F5780]">{active.desc}</p>
              </motion.div>
            </AnimatePresence>

            <div className="mt-4 flex flex-wrap gap-2">
              {HOTSPOTS.map((h) => (
                <button
                  key={h.id}
                  data-testid={`hotspot-chip-${h.id}`}
                  onClick={() => setActive(h)}
                  className={`rounded-full border px-4 py-2 font-nav text-[10px] font-semibold uppercase tracking-[0.15em] transition-all duration-300 ${
                    active.id === h.id
                      ? "border-[#9B7AE8] bg-[#C9B0FF]/40 text-[#4A3A7A]"
                      : "border-[#9B7AE8]/25 bg-white/60 text-[#8E85AC] hover:border-[#9B7AE8] hover:text-[#6B6386]"
                  }`}
                >
                  {h.title}
                </button>
              ))}
            </div>
          </SectionReveal>

          <SectionReveal delay={0.2}>
            <div className="overflow-hidden rounded-2xl border border-[#9B7AE8]/20 bg-white shadow-[0_18px_50px_rgba(155,122,232,0.1)]" data-testid="tech-specs-panel">
              <div className="border-b border-[#9B7AE8]/15 px-7 py-4">
                <span className="font-nav text-[11px] font-semibold uppercase tracking-[0.3em] text-[#9B7AE8]">{t.hardware.specsTitle}</span>
              </div>
              <dl>
                {SPECS.map(([k, v], i) => (
                  <div
                    key={k}
                    data-testid={`spec-row-${i}`}
                    className="flex flex-col gap-1 border-b border-[#9B7AE8]/10 px-7 py-4 last:border-0 sm:flex-row sm:items-center sm:justify-between"
                  >
                    <dt className="font-nav text-[11px] font-medium uppercase tracking-[0.2em] text-[#8E85AC]">{k}</dt>
                    <dd className="font-code text-sm font-semibold text-[#221A3D] sm:text-right">{v}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </SectionReveal>
        </div>
      </div>

      <SectionReveal delay={0.1} className="mt-16 border-t border-[#9B7AE8]/12 pt-16">
          <div className="grid items-start gap-10 lg:grid-cols-[1fr_1.05fr]">
            <div className="order-2 space-y-6 lg:order-1">
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeC.id}
                  data-testid="compact-hotspot-detail-card"
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -16 }}
                  transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                  className="rounded-2xl border border-[#C9B0FF] bg-white/85 p-7 shadow-[0_18px_50px_rgba(155,122,232,0.16)] backdrop-blur-sm"
                >
                  <div className="flex items-center gap-3">
                    <activeC.icon size={22} className="text-[#9B7AE8]" strokeWidth={1.75} />
                    <h4 className="font-display text-xl font-bold text-[#221A3D]">{activeC.title}</h4>
                  </div>
                  {activeC.desc && <p className="mt-3 text-sm leading-relaxed text-[#5F5780]">{activeC.desc}</p>}
                </motion.div>
              </AnimatePresence>

              <div className="flex flex-wrap gap-2">
                {COMPACT_SPOTS.map((h) => (
                  <button
                    key={h.id}
                    data-testid={`compact-hotspot-chip-${h.id}`}
                    onClick={() => setActiveCId(h.id)}
                    className={`rounded-full border px-4 py-2 font-nav text-[10px] font-semibold uppercase tracking-[0.15em] transition-all duration-300 ${
                      activeCId === h.id
                        ? "border-[#9B7AE8] bg-[#C9B0FF]/40 text-[#4A3A7A]"
                        : "border-[#9B7AE8]/25 bg-white/60 text-[#8E85AC] hover:border-[#9B7AE8] hover:text-[#6B6386]"
                    }`}
                  >
                    {h.title}
                  </button>
                ))}
              </div>

              <div className="overflow-hidden rounded-2xl border border-[#9B7AE8]/20 bg-white shadow-[0_18px_50px_rgba(155,122,232,0.1)]" data-testid="compact-specs-panel">
                <div className="border-b border-[#9B7AE8]/15 px-7 py-4">
                  <span className="font-nav text-[11px] font-semibold uppercase tracking-[0.3em] text-[#9B7AE8]">{t.hardware.specsTitle}</span>
                </div>
                <dl>
                  {compact.specs.map(([k, v], i) => (
                    <div
                      key={k}
                      data-testid={`compact-spec-row-${i}`}
                      className="flex flex-col gap-1 border-b border-[#9B7AE8]/10 px-7 py-4 last:border-0 sm:flex-row sm:items-center sm:justify-between"
                    >
                      <dt className="font-nav text-[11px] font-medium uppercase tracking-[0.2em] text-[#8E85AC]">{k}</dt>
                      <dd className="font-code text-sm font-semibold text-[#221A3D] sm:text-right">{v}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            </div>

            <div className="relative order-1 overflow-hidden rounded-[2rem] border border-[#9B7AE8]/20 bg-white shadow-[0_30px_90px_rgba(155,122,232,0.18)] lg:order-2" data-testid="compact-machine-frame">
              <img src="/assets/machine-compact.jpg" alt={compact.alt} className="block w-full" />
              <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_55%,rgba(248,249,250,0.45)_100%)]" />
              {COMPACT_SPOTS.map((h) => (
                <button
                  key={h.id}
                  data-testid={`compact-hotspot-pin-${h.id}`}
                  onClick={() => setActiveCId(h.id)}
                  className="group absolute -translate-x-1/2 -translate-y-1/2"
                  style={{ left: `${h.x}%`, top: `${h.y}%` }}
                  aria-label={h.title}
                >
                  <span className={`animate-pin absolute inset-0 rounded-full ${activeCId === h.id ? "bg-[#C9B0FF]/60" : "bg-[#9B7AE8]/35"}`} />
                  <span
                    className={`relative flex h-9 w-9 items-center justify-center rounded-full border backdrop-blur-md transition-all duration-300 ${
                      activeCId === h.id
                        ? "border-[#9B7AE8] bg-[#9B7AE8] text-white shadow-[0_8px_24px_rgba(155,122,232,0.55)]"
                        : "border-[#9B7AE8]/50 bg-white/85 text-[#9B7AE8] shadow-[0_6px_18px_rgba(155,122,232,0.25)] group-hover:bg-[#C9B0FF]/40"
                    }`}
                  >
                    <h.icon size={15} strokeWidth={2} />
                  </span>
                </button>
              ))}
            </div>
          </div>
        </SectionReveal>
    </section>
  );
}
